import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { get, orderBy, uniqBy } from "lodash";
import styled from "styled-components";
import TextInputField from "../Shared/TextInputField";
import SelectField from "../Shared/SelectField";
import CheckboxField from "../Shared/CheckboxField";
import {
  updateUser,
  createUser,
  deleteUser,
  getUsersByPage,
  getUserLoginExperience,
  updateUserLoginExperience,
  getUserFirebaseAuth,
  updateUserEmailVerification,
} from "../../services/auth";
import { userRoles, timezones, getLabelByValue } from "../../constants";
import { selectActiveSpecialists, selectSpecialistsById } from "../appSlice";
import { Alert } from "antd";

const isDataFromPMS = process.env.REACT_APP_DATA_SOURCE === "pms-api";

const Coordinators = () => {
  const referrers = useSelector((state) => state.app.referrers);
  const clinics = useSelector((state) => orderBy(state.app.clinics, ["name"], ["asc"]));
  const specialists = useSelector(selectSpecialistsById);
  const activeSpecialists = useSelector(selectActiveSpecialists);
  const admins = useSelector((state) => state.app.admins);
  const nurses = useSelector((state) => state.app.nurses);
  const supervisors = useSelector((state) => state.app.supervisors);
  const adminMappingUsers = useMemo(
    () =>
      orderBy(uniqBy([...admins, ...nurses], "userId"), [
        (user) => user.firstName?.toLowerCase(),
        (user) => user.lastName?.toLowerCase(),
      ]),
    [admins, nurses]
  );
  const [referrerId, setReferrerId] = useState("");
  const [pmsUserId, setPmsUserId] = useState("");
  const [clinicId, setClinicId] = useState("");
  const [specialistId, setSpecialistId] = useState("");
  const [coordinators, setCoordinators] = useState([]);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [shortName, setShortName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [timezone, setTimezone] = useState("");
  const [isDisabled, setIsDisabled] = useState(false);
  const [sendEmailToSpecialist, setSendEmailToSpecialist] = useState(true);
  const [activeCoordinator, setActiveCoordinator] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [totalNumOfRows, setTotalNumOfRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pageFrom, setPageFrom] = useState(1);
  const [pageTo, setPageTo] = useState(10);
  const [isError, setIsError] = useState(false);
  const [filterRole, setFilterRole] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showDeactivatedUsers, setShowDeactivatedUsers] = useState(false);
  const [newLoginExperienceEnabled, setNewLoginExperienceEnabled] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [mfaPhoneNumber, setMfaPhoneNumber] = useState("");
  const [isUpdatingLoginExperience, setIsUpdatingLoginExperience] = useState(false);
  const [loginExperienceError, setLoginExperienceError] = useState("");

  const fetchUsers = async () => {
    const { users, totalUsers } = await getUsersByPage(
      {
        curr: currentPageIndex,
        size: pageSize,
      },
      {
        role: filterRole,
        search: searchQuery,
        includeDisabled: showDeactivatedUsers,
      }
    );
    setCoordinators(users);
    setTotalNumOfRows(totalUsers);
    setTotalPages(Math.ceil(totalUsers / pageSize));

    const offset = totalUsers % pageSize;
    const offsetPageSize = offset === 0 ? pageSize : offset;

    setPageFrom(currentPageIndex * pageSize + 1);
    setPageTo(
      currentPageIndex === Math.ceil(totalUsers / pageSize) - 1
        ? currentPageIndex * pageSize + offsetPageSize
        : currentPageIndex * pageSize + pageSize
    );
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPageIndex, pageSize, filterRole, searchQuery, showDeactivatedUsers]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setCurrentPageIndex(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (activeCoordinator) {
      setFirstName(activeCoordinator.firstName || "");
      setLastName(activeCoordinator.lastName || "");
      setRole(activeCoordinator.role || "");
      setShortName(activeCoordinator.shortName || "");
      setEmail(activeCoordinator.email || "");
      setTimezone(activeCoordinator.timezone || "");
      setIsDisabled(activeCoordinator.disabled || false);
      setReferrerId(
        activeCoordinator.role === "GP" && activeCoordinator.coreplusId ? activeCoordinator.coreplusId : ""
      );
      setClinicId(
        activeCoordinator.role === "CLINIC_ADMIN" && activeCoordinator.coreplusId ? activeCoordinator.coreplusId : ""
      );
      setSpecialistId(
        activeCoordinator.role === "SPECIALIST" && activeCoordinator.coreplusId ? activeCoordinator.coreplusId : ""
      );
      setPmsUserId(activeCoordinator.pmsId || "");
      setNewLoginExperienceEnabled(false);
      setEmailVerified(false);
      setMfaPhoneNumber("");
      setLoginExperienceError("");

      const loadFirebaseAuth = async () => {
        try {
          const authInfo = await getUserFirebaseAuth(activeCoordinator.uid);
          setEmailVerified(!!authInfo.emailVerified);
        } catch (error) {
          setEmailVerified(false);
        }
      };

      const loadLoginExperience = async () => {
        try {
          const authInfo = await getUserLoginExperience(activeCoordinator.uid);
          setNewLoginExperienceEnabled(!!authInfo.newLoginExperienceEnabled);
          setMfaPhoneNumber(authInfo.mfaPhoneNumber || authInfo.firebaseMfaPhoneNumber || "");
        } catch (error) {
          setLoginExperienceError("Unable to load login experience settings.");
        }
      };

      loadFirebaseAuth();
      loadLoginExperience();
    }
  }, [activeCoordinator]);

  useEffect(() => {
    if (referrers && referrers.length && role === "GP" && referrerId) {
      const referrer = referrers.find((r) => r.id === referrerId);
      if (!referrer) return;
      setFirstName(referrer.firstName);
      setLastName(referrer.lastName);
      setClinicId("");
    }
  }, [referrerId, role, referrers]);

  useEffect(() => {
    if (clinics && clinics.length && role === "CLINIC_ADMIN" && clinicId) {
      setClinicId(clinicId);
      setReferrerId("");
    }
  }, [clinicId, role, clinics]);

  // The specialist saved on the account being edited, if it is a specialist account.
  const savedSpecialistId =
    get(activeCoordinator, "role") === "SPECIALIST" ? get(activeCoordinator, "coreplusId", "") : "";

  // Picking a specialist fills in their details. Loading an account never does,
  // and reselecting the account's own specialist brings back what was saved.
  const handleChangeSpecialist = (nextSpecialistId) => {
    setSpecialistId(nextSpecialistId);
    if (savedSpecialistId && nextSpecialistId === savedSpecialistId) {
      setFirstName(activeCoordinator.firstName || "");
      setLastName(activeCoordinator.lastName || "");
      setShortName(activeCoordinator.shortName || "");
      setEmail(activeCoordinator.email || "");
      setTimezone(activeCoordinator.timezone || "");
      return;
    }
    const specialist = get(specialists, nextSpecialistId);
    if (!specialist) return;
    const specialistFirstName = specialist.firstName || "";
    const specialistLastName = specialist.lastName || "";
    setFirstName(specialistFirstName);
    setLastName(specialistLastName);
    setShortName(`${specialistFirstName.toUpperCase().charAt(0)}${specialistLastName.toUpperCase()}`);
    setEmail(specialist.email || "");
    setTimezone(get(specialist, "appointmentLength.timezone", ""));
  };

  // Coming back to Specialist refills from the specialist still selected, so the
  // name and email cannot be left over from another role.
  const handleChangeRole = (nextRole) => {
    setRole(nextRole);
    if (nextRole === "SPECIALIST" && specialistId) {
      handleChangeSpecialist(specialistId);
    }
  };

  const handleClickRow = (coordinator) => {
    setActiveCoordinator({ ...coordinator });
  };

  const handleClickReset = () => {
    setActiveCoordinator(null);
    setFirstName("");
    setLastName("");
    setShortName("");
    setEmail("");
    setRole("");
    setTimezone("");
    setIsDisabled(false);
    setReferrerId("");
    setSpecialistId("");
    setClinicId("");
    setPmsUserId("");
    setPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setSendEmailToSpecialist(true);
    setNewLoginExperienceEnabled(false);
    setEmailVerified(false);
    setMfaPhoneNumber("");
    setLoginExperienceError("");
  };

  const handleMigrateToNewLoginExperience = async () => {
    if (!activeCoordinator || isUpdatingLoginExperience || newLoginExperienceEnabled) {
      return;
    }

    setIsUpdatingLoginExperience(true);
    setLoginExperienceError("");

    try {
      const result = await updateUserLoginExperience(activeCoordinator.uid, true);
      setNewLoginExperienceEnabled(!!result.newLoginExperienceEnabled);
      setMfaPhoneNumber(result.mfaPhoneNumber || mfaPhoneNumber);
      if (!result.mfaPhoneNumber) {
        setMfaPhoneNumber("");
      }
    } catch (error) {
      setLoginExperienceError(error.response?.data?.message || "Failed to migrate to new login experience.");
    } finally {
      setIsUpdatingLoginExperience(false);
    }
  };

  const handleClickSubmit = async () => {
    setIsProcessing(true);
    try {
      if (activeCoordinator) {
        // check password
        if (password.length > 0 || confirmPassword.length > 0) {
          if (password !== confirmPassword) {
            setPasswordError("Passwords doesn't match.");
            setIsProcessing(false);
            return;
          }
        }
        // update user
        await updateUser({
          uid: activeCoordinator.uid,
          firstName,
          lastName,
          shortName,
          role,
          email,
          timezone,
          password,
          referrerId,
          clinicId,
          disabled: isDisabled,
          specialistId,
          pmsUserId,
        });
        await updateUserEmailVerification(activeCoordinator.uid, emailVerified);
      } else {
        // create user
        await createUser({
          firstName,
          lastName,
          shortName,
          role,
          email,
          timezone,
          referrerId,
          clinicId,
          disabled: isDisabled,
          sendEmailToSpecialist,
          specialistId,
          pmsUserId,
        });
      }
      await fetchUsers();
    } catch (e) {
      setIsError(true);
    } finally {
      handleClickReset();
      setIsProcessing(false);
    }
  };

  const handleClickDelete = async () => {
    if (activeCoordinator) {
      setIsDeleting(true);
      await deleteUser(activeCoordinator.uid);
      await fetchUsers();
      handleClickReset();
      setIsDeleting(false);
    }
  };

  const handleChangeDisabled = () => {
    setIsDisabled((isDisabled) => !isDisabled);
  };

  const handleChangeEmailVerified = () => {
    setEmailVerified((verified) => !verified);
  };

  const handleChangeSendEmailToSpecialist = () => setSendEmailToSpecialist((pre) => !pre);

  // Only active specialists can be picked; the one saved on the account being
  // edited stays listed even if Sync has since deactivated them.
  const linkedSpecialist =
    savedSpecialistId && !activeSpecialists[savedSpecialistId] ? specialists[savedSpecialistId] : null;
  const specialistOptions = [...Object.values(activeSpecialists), ...(linkedSpecialist ? [linkedSpecialist] : [])].map(
    (s) => ({
      label: `${s.firstName} ${s.lastName}`,
      value: s.id,
    })
  );

  const handleChangeShowDeactivatedUsers = () => {
    setShowDeactivatedUsers((show) => !show);
    setCurrentPageIndex(0);
  };

  const handleClearRoleFilter = () => {
    setFilterRole("");
    setCurrentPageIndex(0);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchQuery("");
    setCurrentPageIndex(0);
  };
  const onErrorClose = () => setIsError(false);
  return (
    <>
      <div className="columns">
        <div className="column">
          <div className="is-size-7">UID: {activeCoordinator && activeCoordinator.uid}</div>
        </div>
      </div>
      <div className="columns">
        <div className="column is-one-third">
          <SelectField
            label="Role"
            isRequired
            value={role}
            options={userRoles}
            onChange={(e) => handleChangeRole(e.target.value)}
          />
        </div>
        {role === "GP" ? (
          <div className="column is-one-third">
            <SelectField
              label="Referrer"
              isRequired
              value={referrerId}
              options={referrers.map((r) => ({ label: `${r.firstName} ${r.lastName}`, value: r.id }))}
              onChange={(e) => setReferrerId(e.target.value)}
            />
          </div>
        ) : null}
        {isDataFromPMS && role === "TELECARE_ADMIN" ? (
          <div className="column is-one-third">
            <SelectField
              label="Admin"
              isRequired
              value={pmsUserId}
              options={adminMappingUsers.map((c) => ({ label: `${c.firstName} ${c.lastName}`, value: c.userId }))}
              onChange={(e) => setPmsUserId(e.target.value)}
            />
          </div>
        ) : null}
        {isDataFromPMS && role === "TELECARE_SUPERVISOR" ? (
          <div className="column is-one-third">
            <SelectField
              label="Supervisor"
              value={pmsUserId}
              options={supervisors.map((c) => ({ label: `${c.firstName} ${c.lastName}`, value: c.userId }))}
              onChange={(e) => setPmsUserId(e.target.value)}
            />
          </div>
        ) : null}
        {role === "CLINIC_ADMIN" ? (
          <div className="column is-one-third">
            <SelectField
              label="Clinic"
              isRequired
              value={clinicId}
              options={clinics.map((c) => ({ label: c.name, value: c.id }))}
              onChange={(e) => setClinicId(e.target.value)}
            />
          </div>
        ) : null}
        {role === "SPECIALIST" && (
          <div className="column is-one-third">
            <SelectField
              label="Specialist"
              isRequired
              value={specialistId}
              options={specialistOptions}
              onChange={(e) => handleChangeSpecialist(e.target.value)}
            />
          </div>
        )}
      </div>
      <div className="columns">
        <div className="column">
          <TextInputField
            label="First name"
            isRequired
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </div>
        <div className="column">
          <TextInputField label="Last name" isRequired value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
        <div className="column">
          <TextInputField label="Short name" value={shortName} onChange={(e) => setShortName(e.target.value)} />
        </div>
      </div>
      <div className="columns">
        <div className="column is-one-third">
          <TextInputField label="Email" isRequired value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="column is-one-third">
          <SelectField
            label="Timezone"
            isRequired
            value={timezone}
            options={timezones}
            onChange={(e) => setTimezone(e.target.value)}
          />
        </div>
      </div>
      {activeCoordinator ? (
        <div className="columns">
          {newLoginExperienceEnabled && (
            <div className="column is-one-third">
              <TextInputField
                label="MFA phone number"
                value={mfaPhoneNumber || "Not set — user will enter on first sign-in"}
                onChange={() => {}}
                disabled
              />
            </div>
          )}
          {!newLoginExperienceEnabled && (
            <div className="column is-one-third">
              <div className="field">
                <label className="label is-small">New login experience</label>
                <div className="control">
                  <button
                    type="button"
                    className={`button is-link is-small is-radiusless ${isUpdatingLoginExperience ? "is-loading" : ""}`}
                    onClick={handleMigrateToNewLoginExperience}
                    disabled={isUpdatingLoginExperience}
                  >
                    Migrate to new login experience
                  </button>
                </div>
                {loginExperienceError && <p className="help is-danger is-size-7">{loginExperienceError}</p>}
              </div>
            </div>
          )}
        </div>
      ) : null}
      {activeCoordinator ? (
        <div className="columns">
          <div className="column is-one-third">
            <div className="box is-radiusless">
              <div className="content is-size-7">
                Please leave these 2 fields blank if you don't want to change password.
                <br />
                Password must be at lease 6 characters long.
              </div>
              <div className="mb-4">
                <TextInputField
                  type="password"
                  label="Password"
                  isRequired={!!confirmPassword}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <div>
                <TextInputField
                  type="password"
                  label="Confirm Password"
                  isRequired={!!password}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              {passwordError.length > 0 ? <div className="has-text-danger is-size-7">{passwordError}</div> : null}
            </div>
          </div>
        </div>
      ) : null}
      <div className="columns">
        <div className="column is-one-third">
          <CheckboxField label="Disabled" checked={isDisabled} onChange={handleChangeDisabled} />
        </div>
        {activeCoordinator && (
          <div className="column is-one-third">
            <CheckboxField label="Email verified" checked={emailVerified} onChange={handleChangeEmailVerified} />
          </div>
        )}
        {role === "SPECIALIST" && !activeCoordinator && (
          <div className="column is-one-third">
            <CheckboxField
              label="Email notification"
              checked={sendEmailToSpecialist}
              onChange={handleChangeSendEmailToSpecialist}
            />
          </div>
        )}
      </div>
      <div className="buttons">
        <button
          className={`button ${isProcessing ? "is-loading " : ""}is-small is-radiusless is-info`}
          disabled={
            !firstName ||
            !lastName ||
            !email ||
            !role ||
            !timezone ||
            (role === "GP" && !referrerId) ||
            (role === "CLINIC_ADMIN" && !clinicId) ||
            (role === "SPECIALIST" && !specialistId)
          }
          onClick={handleClickSubmit}
        >
          {activeCoordinator ? "Update" : "Create"}
        </button>
        <button className="button is-small is-radiusless" onClick={handleClickReset}>
          Reset
        </button>
        {activeCoordinator ? (
          <button
            className={`button is-danger is-small is-radiusless ${isDeleting ? "is-loading" : ""}`}
            onClick={handleClickDelete}
          >
            Delete
          </button>
        ) : null}
      </div>
      {isError && (
        <div className="columns">
          <div className="column is-one-third">
            <Alert message="Ops! Something went wrong." type="error" closable onClose={onErrorClose} />
          </div>
        </div>
      )}
      <hr />
      <div className="columns is-align-items-end">
        <div className="column is-one-third">
          <TextInputField
            label="Search users"
            placeholder="Name or user ID"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <div className="column is-narrow">
          <button
            className="button is-small is-radiusless"
            type="button"
            onClick={handleClearSearch}
            disabled={!searchInput}
          >
            Clear search
          </button>
        </div>
        <div className="column is-narrow">
          <CheckboxField
            label="Show deactivated users"
            checked={showDeactivatedUsers}
            onChange={handleChangeShowDeactivatedUsers}
          />
        </div>
      </div>
      <div className="level-left">
        <SelectField
          label="Choose a filter"
          value={filterRole}
          options={userRoles}
          onChange={(e) => {
            setFilterRole(e.target.value);
            setCurrentPageIndex(0);
          }}
        />
        <button
          className="button is-primary is-radiusless is-size-7 mt-3 ml-3"
          aria-label="close"
          onClick={handleClearRoleFilter}
        >
          Clear Sorting
        </button>
      </div>
      <StyledTable className="table is-hoverable is-striped is-size-7">
        <thead>
          <tr>
            <th>Full name</th>
            <th>Short name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Timezone</th>
          </tr>
        </thead>
        <tbody>
          {coordinators.map((coordinator) => (
            <tr
              className={`${
                activeCoordinator && coordinator.uid === activeCoordinator.uid ? "has-background-info-light" : ""
              }`}
              key={coordinator.uid}
              onClick={() => handleClickRow(coordinator)}
            >
              <td>
                <span className="icon-text">
                  <span className={`icon ${coordinator.disabled ? "has-text-danger" : "has-text-info"}`}>
                    <i className="fas fa-user"></i>
                  </span>
                  <span>{`${coordinator.firstName || ""} ${coordinator.lastName || ""}`}</span>
                </span>
              </td>
              <td>{coordinator.shortName || ""}</td>
              <td>{coordinator.email || ""}</td>
              <td>{getLabelByValue(coordinator.role) || ""}</td>
              <td>{coordinator.timezone || ""}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={12}>
              <div className="level">
                <div className="level-right"></div>
                <div className="level-right">
                  <div className="level-item">Rows per page:</div>
                  <div className="level-item">
                    <div className="field">
                      <div className="control">
                        <div className="select is-small">
                          <select
                            className="is-radiusless"
                            onChange={(e) => {
                              setCurrentPageIndex(0);
                              setPageSize(Number(e.target.value));
                            }}
                            value={pageSize}
                          >
                            <option value="10">10</option>
                            <option value="20">20</option>
                            <option value="30">30</option>
                            <option value="40">40</option>
                            <option value="50">50</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="level-item mx-4">
                    <span>{pageFrom}</span>
                    <span className="mx-1">-</span>
                    <span>{pageTo}</span>
                    <span className="mx-1">of</span>
                    <span>{totalNumOfRows}</span>
                  </div>
                  <div className="level-item mx-4">
                    <div className="go-start" onClick={() => setCurrentPageIndex(0)}>
                      <span className="icon is-medium has-text-primary">
                        <i className="fas fa-step-backward fa-lg"></i>
                      </span>
                    </div>
                    <div
                      className="go-previous"
                      onClick={() => {
                        setCurrentPageIndex((i) => (i === 0 ? i : i - 1));
                      }}
                    >
                      <span className="icon is-medium has-text-primary">
                        <i className="fas fa-chevron-left fa-lg"></i>
                      </span>
                    </div>
                    <div
                      className="go-next"
                      onClick={() =>
                        setCurrentPageIndex((i) => {
                          return i === totalPages - 1 ? i : i + 1;
                        })
                      }
                    >
                      <span className="icon is-medium has-text-primary">
                        <i className="fas fa-chevron-right fa-lg"></i>
                      </span>
                    </div>
                    <div className="go-end" onClick={() => setCurrentPageIndex(totalPages - 1)}>
                      <span className="icon is-medium has-text-primary">
                        <i className="fas fa-step-forward fa-lg"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tfoot>
      </StyledTable>
    </>
  );
};

const StyledTable = styled.table`
  th:nth-child(1) {
    width: 20%;
  }
  th:nth-child(2) {
    width: 10%;
  }
  th:nth-child(3) {
    width: 20%;
  }
  th:nth-child(4) {
    width: 20%;
  }
  th:nth-child(5) {
    width: 20%;
  }
  tbody tr:hover {
    cursor: pointer;
  }
`;

export default Coordinators;
